/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { cssMap, jsx, cx } from '@atlaskit/css';
import { Box, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type Integration, ScopeState } from '../../types';
import { useAnalytics } from '../../analytics';
import { messages } from '../i18n';
import { GOOGLE_SERVICE, MICROSOFT_SERVICE, PLATFORM_SLACK_SERVICE } from './constants';
import { GoogleIcon } from './images/google';
import { MicrosoftIcon } from './images/microsoft';
import { SlackIcon } from './images/slack';
import { useThirdPartyState } from './ThirdPartyContext';
import UpdateConnection from './UpdateConnection';
import { ThirdPartyAnalytics } from './ThirdPartyAnalytics';
import { getIsInfiSpotlightSeen, setIsInfiSpotlightSeen } from '../InfiSpotlight/localStorageUtils';
import InfiSpotlight from '../InfiSpotlight';
import { useInfiSpotlight } from '../InfiSpotlight/useInfiSpotlight';
import { useInvitePeopleContext } from '../InvitePeople';

const ICON_SIZE = {
	height: 16,
	width: 16,
};

const styles = cssMap({
	wrapper: {
		color: token('color.text.subtlest', '#6B778C'),
		font: token(
			'font.body',
			'normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
	},
	header: {
		font: token(
			'font.heading.xxsmall',
			'normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		fontWeight: token('font.weight.semibold', '600'),
		marginBottom: token('space.050', '4px'),
		paddingInline: token('space.0', '0px'),
	},
	buttonGroup: {
		marginInline: token('space.negative.025', '-2px'),
		listStyleType: 'none',
		marginTop: token('space.0'),
		marginBottom: token('space.0'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	buttonIcon: {
		marginBlock: token('space.100', '8px'),
		marginRight: token('space.100', '8px'),
	},
	connectButtonWrapper: {
		display: 'contents',
	},
	connectButton: {
		display: 'flex',
		flex: '1 1',
		marginBlock: token('space.050', '4px'),
		marginInline: token('space.025', '2px'),
		height: '40px',
		alignItems: 'center',
		justifyContent: 'center',
		borderColor: token('color.border', 'rgba(9,30,66,0.14)'),
		borderRadius: token('radius.small', '3px'),
		borderStyle: 'solid',
		borderWidth: token('border.width', '1px'),
		backgroundColor: token('color.background.input', '#FFFFFF'),
		'&:hover': {
			borderWidth: token('border.width', '1px'),
			borderColor: token('color.border', 'rgba(9,30,66,0.14)'),
			backgroundColor: token('color.background.input.hovered', '#F7F8F9'),
		},
		'&:active': {
			borderWidth: token('border.width', '1px'),
			borderColor: token('color.border', 'rgba(9,30,66,0.14)'),
			backgroundColor: token('color.background.neutral.pressed', 'rgba(9,30,66,0.31)'),
		},
	},
	connectButtonSmall: {
		display: 'flex',
		flex: '1 1',
		marginBlock: token('space.050', '4px'),
		marginInline: token('space.025', '2px'),
		height: '16px',
		alignItems: 'center',
		justifyContent: 'center',
		borderStyle: 'hidden',
		backgroundColor: token('color.background.input', '#FFFFFF'),
		'&:hover': {
			borderWidth: token('border.width', '1px'),
			borderColor: token('color.border', 'rgba(9,30,66,0.14)'),
			backgroundColor: token('color.background.input.hovered', '#F7F8F9'),
		},
		'&:active': {
			borderWidth: token('border.width', '1px'),
			borderColor: token('color.border', 'rgba(9,30,66,0.14)'),
			backgroundColor: token('color.background.neutral.pressed', 'rgba(9,30,66,0.31)'),
		},
	},
	headerSmall: {
		font: token(
			'font.heading.xxsmall',
			'normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		fontWeight: token('font.weight.semibold', '600'),
		marginBottom: token('space.050', '4px'),
		marginTop: token('space.050', '4px'),
		paddingInline: token('space.0', '0px'),
	},
});

const ThirdPartyConnect = () => {
	const analytics = useAnalytics(ThirdPartyAnalytics);
	const { formatMessage } = useIntl();
	const {
		enabledIntegrations,
		onConnectProvider,
		updateIntegrations,
		hideThirdPartyConnectLegend,
	} = useThirdPartyState();
	const { isEnabled: isInfiSpotlightEnabled } = useInfiSpotlight();
	const { experimental_inlineThirdPartyButtons } = useInvitePeopleContext();

	const handleClick = useCallback(
		(serviceKey: Integration) => () => {
			if (onConnectProvider) {
				analytics.connectButtonClicked(serviceKey);
				onConnectProvider({
					serviceKey,
				});
			}
		},
		[analytics, onConnectProvider],
	);

	const [spotlightHidden, setSpotlightHidden] = useState(!!getIsInfiSpotlightSeen());

	useEffect(() => {
		if (isInfiSpotlightEnabled && spotlightHidden) {
			setIsInfiSpotlightSeen(true);
		}
	}, [isInfiSpotlightEnabled, spotlightHidden]);

	const dismissSpotlight = useCallback(() => {
		if (isInfiSpotlightEnabled) {
			setSpotlightHidden(true);
		}
	}, [isInfiSpotlightEnabled]);

	const wrapWithNudgeToInvite = (slackButton: ReactNode) => slackButton;
	const googleButton = (
		<Box xcss={styles.connectButtonWrapper} as="li">
			<Pressable
				onClick={handleClick(GOOGLE_SERVICE)}
				testId="testId-invite-people-connect-google"
				xcss={cx(
					styles.connectButton,
					experimental_inlineThirdPartyButtons && styles.connectButtonSmall,
				)}
			>
				<Inline as="span" alignBlock="center" alignInline="center">
					<GoogleIcon {...ICON_SIZE} css={styles.buttonIcon} />
					<Text color="color.text" size="medium" weight="medium">
						{formatMessage(messages.googleThirdPartyButton)}
					</Text>
				</Inline>
			</Pressable>
		</Box>
	);
	const slackButton = (
		<Box as="li" xcss={styles.connectButtonWrapper}>
			<Pressable
				onClick={handleClick(PLATFORM_SLACK_SERVICE)}
				testId={`testId-invite-people-connect-slack`}
				xcss={cx(
					styles.connectButton,
					experimental_inlineThirdPartyButtons && styles.connectButtonSmall,
				)}
			>
				<Inline as="span" alignBlock="center" alignInline="center">
					<SlackIcon {...ICON_SIZE} css={styles.buttonIcon} />
					<Text color="color.text" size="medium" weight="medium">
						{formatMessage(messages.slackThirdPartyButton)}
					</Text>
				</Inline>
			</Pressable>
		</Box>
	);
	const microsoftButton = (
		<Box as="li" xcss={styles.connectButtonWrapper}>
			<Pressable
				onClick={handleClick(MICROSOFT_SERVICE)}
				testId="testId-invite-people-connect-microsoft"
				xcss={cx(
					styles.connectButton,
					experimental_inlineThirdPartyButtons && styles.connectButtonSmall,
				)}
			>
				<Inline as="span" alignBlock="center" alignInline="center">
					<MicrosoftIcon {...ICON_SIZE} css={styles.buttonIcon} />
					<Text color="color.text" size="medium" weight="medium">
						{formatMessage(messages.microsoftThirdPartyButton)}
					</Text>
				</Inline>
			</Pressable>
		</Box>
	);

	const buttonGroup = (
		<Inline shouldWrap spread="space-between" xcss={styles.buttonGroup} as="ul">
			{enabledIntegrations.has(GOOGLE_SERVICE) && googleButton}
			{enabledIntegrations.has(PLATFORM_SLACK_SERVICE) && wrapWithNudgeToInvite(slackButton)}
			{enabledIntegrations.has(MICROSOFT_SERVICE) && microsoftButton}
		</Inline>
	);

	const inLineLegendAndButtonGroupSmall = (
		<Inline shouldWrap spread="space-between" xcss={styles.buttonGroup} as="ul">
			<Box as="legend" xcss={styles.headerSmall}>
				{formatMessage(messages.thirdPartyConnect)}
			</Box>
			{enabledIntegrations.has(GOOGLE_SERVICE) && googleButton}
			{enabledIntegrations.has(PLATFORM_SLACK_SERVICE) && wrapWithNudgeToInvite(slackButton)}
			{enabledIntegrations.has(MICROSOFT_SERVICE) && microsoftButton}
		</Inline>
	);

	if (experimental_inlineThirdPartyButtons) {
		return <fieldset css={styles.wrapper}>{inLineLegendAndButtonGroupSmall}</fieldset>;
	}

	return (
		<fieldset css={styles.wrapper}>
			{!hideThirdPartyConnectLegend && (
				<Box as="legend" xcss={styles.header}>
					{formatMessage(messages.thirdPartyConnect)}
				</Box>
			)}
			{isInfiSpotlightEnabled ? (
				<InfiSpotlight spotlightHidden={spotlightHidden} dismissSpotlight={dismissSpotlight}>
					{buttonGroup}
				</InfiSpotlight>
			) : (
				buttonGroup
			)}
			{!!updateIntegrations.noScope.size && <UpdateConnection scope={ScopeState.NO_SCOPE} />}
		</fieldset>
	);
};

export default ThirdPartyConnect;
